import type { ProjectDefinition } from "../../../types";
import { type FuncionProjectSpec, conditions, seededRandom } from "../spec";

const SHEET = "BR Precios";

const PRODUCTOS: [string, string, number][] = [
  ["Papel bond carta (caja)", "Oficina", 489],
  ["Tóner negro", "Cómputo", 1290],
  ["Folders tamaño carta", "Oficina", 159],
  ["Engrapadora metálica", "Oficina", 229],
  ["Mouse inalámbrico", "Cómputo", 349],
  ["Teclado USB", "Cómputo", 299],
  ["Memoria USB 64 GB", "Cómputo", 189],
  ["Limpiador multiusos", "Limpieza", 89],
  ["Toallas de papel", "Limpieza", 129],
  ["Bolsas para basura", "Limpieza", 99],
  ["Café molido 1 kg", "Cafetería", 289],
  ["Vasos desechables", "Cafetería", 79],
  ["Azúcar 2 kg", "Cafetería", 69],
  ["Plumas azules (caja)", "Oficina", 119],
  ["Marcatextos (paquete)", "Oficina", 99],
  ["Cable HDMI", "Cómputo", 199],
  ["Hub USB-C", "Cómputo", 799],
  ["Desinfectante en aerosol", "Limpieza", 139],
  ["Jabón líquido", "Limpieza", 109],
  ["Té surtido", "Cafetería", 149],
  ["Galletas surtidas", "Cafetería", 119],
  ["Cuaderno profesional", "Oficina", 59],
  ["Calculadora de escritorio", "Oficina", 259],
  ["Audífonos con micrófono", "Cómputo", 449],
  ["Escoba", "Limpieza", 119],
  ["Trapeador", "Limpieza", 159],
  ["Crema para café", "Cafetería", 99],
  ["Notas adhesivas", "Oficina", 69],
  ["Regulador de voltaje", "Cómputo", 599],
  ["Cafetera de goteo", "Cafetería", 899],
];

const rnd = seededRandom(1515);
// Código, Producto, Categoría, Precio, Existencia
const LISTA = PRODUCTOS.map(([producto, categoria, precio], i) => [
  `P-${101 + i}`,
  producto,
  categoria,
  precio,
  rnd.int(0, 250),
] as [string, string, string, number, number]);

// Peso desde (kg), Costo — ascending, as approximate BUSCARV requires.
const TARIFAS: [number, number][] = [
  [0, 99],
  [1, 149],
  [5, 249],
  [10, 399],
  [20, 649],
  [30, 899],
];

// Pedido, Código, Cantidad, Peso kg
const PEDIDOS = Array.from({ length: 25 }, (_, i) => [
  `PED-${5001 + i}`,
  rnd.pick(LISTA)[0],
  rnd.int(1, 30),
  rnd.int(1, 80) / 2,
] as [string, string, number, number]);

const IN = { codigo: "P-999", texto: "café" };
const prod = (codigo: string) => LISTA.find((r) => r[0] === codigo)!;
const tarifa = (peso: number) => [...TARIFAS].reverse().find(([desde]) => peso >= desde)![1];
const contiene = LISTA.find((r) => r[1].toLowerCase().includes(IN.texto));

export const spec: FuncionProjectSpec = {
  projectId: "funciones-buscarv",
  sheets: [
    {
      name: SHEET,
      title: "Lista de precios y pedidos",
      subtitle: "Proveeduría de oficina — completa los pedidos buscando en la lista de precios",
      tables: [
        {
          name: "ListaPrecios",
          anchor: "A4",
          headers: ["Código", "Producto", "Categoría", "Precio", "Existencia"],
          rows: LISTA,
          formats: { Precio: "$#,##0" },
        },
        {
          name: "TarifasEnvio",
          anchor: "G4",
          headers: ["Peso desde", "Costo"],
          rows: TARIFAS,
          formats: { Costo: "$#,##0" },
        },
        {
          name: "Pedidos",
          anchor: "J4",
          headers: ["Pedido", "Código", "Cantidad", "Peso kg"],
          rows: PEDIDOS,
          answerColumns: ["Producto", "Precio", "Importe", "Costo envío", "Categoría"],
          formats: { Precio: "$#,##0", Importe: "$#,##0", "Costo envío": "$#,##0" },
        },
      ],
      inputs: [
        { address: "U4", label: "Código a consultar", value: IN.codigo },
        { address: "U5", label: "Texto a buscar", value: IN.texto },
      ],
      answers: [
        { address: "U8", label: "Tarea 5 · Producto del código" },
        { address: "U9", label: "Tarea 7 · Precio del primero que contiene", format: "$#,##0" },
      ],
    },
  ],
  checks: {
    1: {
      kind: "column", sheet: SHEET, table: "Pedidos", column: "Producto", fns: ["VLOOKUP"],
      expected: PEDIDOS.map((p) => prod(p[1])[1]),
    },
    2: {
      kind: "column", sheet: SHEET, table: "Pedidos", column: "Precio", fns: ["VLOOKUP"],
      expected: PEDIDOS.map((p) => prod(p[1])[3]),
    },
    3: {
      kind: "column", sheet: SHEET, table: "Pedidos", column: "Importe", fns: ["VLOOKUP"],
      expected: PEDIDOS.map((p) => p[2] * prod(p[1])[3]),
    },
    4: {
      kind: "column", sheet: SHEET, table: "Pedidos", column: "Costo envío", fns: ["VLOOKUP"],
      expected: PEDIDOS.map((p) => tarifa(p[3])),
    },
    5: { kind: "cell", sheet: SHEET, address: "U8", fns: ["VLOOKUP", "IFNA"], expected: "No existe" },
    6: {
      kind: "column", sheet: SHEET, table: "Pedidos", column: "Categoría", fns: ["VLOOKUP", "MATCH"],
      expected: PEDIDOS.map((p) => prod(p[1])[2]),
    },
    7: { kind: "cell", sheet: SHEET, address: "U9", fns: ["VLOOKUP"], expected: contiene![3] },
  },
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-buscarv",
  title: "BUSCARV: Lista de precios y pedidos",
  subtitle: "La búsqueda clásica, bien usada",
  description:
    "Llevas la proveeduría de una empresa. Tienes la lista de precios, una tabla de tarifas de envío por peso y 25 pedidos a los que les falta todo: nombre del producto, precio, importe, costo de envío y categoría.\n\nBUSCARV sigue en millones de archivos. Aprenderás a usarla bien: coincidencia exacta con FALSO, aproximada para tarifas, el número de columna calculado con COINCIDIR y qué hacer cuando no encuentra nada.",
  topics: [
    "Coincidencia exacta con FALSO",
    "Usar el resultado en un cálculo",
    "Coincidencia aproximada para tarifas por tramos",
    "SI.ND cuando no existe el código",
    "Número de columna con COINCIDIR",
    "Comodines en el valor buscado",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: Nombre del producto",
      description:
        "En la hoja <strong>BR Precios</strong>, tabla <strong>Pedidos</strong>, columna <strong>Producto</strong>: trae el nombre del producto de cada pedido buscando su <strong>Código</strong> en <strong>ListaPrecios</strong>.",
      solution: [
        `Selecciona ${F("N5")}.`,
        "BUSCARV(valor, tabla, número de columna, FALSO): busca en la primera columna de la tabla y devuelve la columna que le indiques. Producto es la columna 2.",
        `${F("FALSO")} pide coincidencia exacta. Si lo omites, BUSCARV hace una búsqueda aproximada y puede traer un producto equivocado.`,
        `Escribe ${F("=BUSCARV([@Código],ListaPrecios,2,FALSO)")}.`,
      ],
      expectedResult: "Nombre del producto de cada pedido",
      finalFormula: "=BUSCARV([@Código],ListaPrecios,2,FALSO)",
    },
    {
      id: 2,
      level: "basico",
      title: "Tarea 2: Precio unitario",
      description: "En la columna <strong>Precio</strong>: trae el precio de lista de cada producto.",
      solution: [
        `Selecciona ${F("O5")}.`,
        "Precio es la 4.ª columna de ListaPrecios.",
        `Escribe ${F("=BUSCARV([@Código],ListaPrecios,4,FALSO)")}.`,
      ],
      expectedResult: "Precio de cada producto",
      finalFormula: "=BUSCARV([@Código],ListaPrecios,4,FALSO)",
    },
    {
      id: 3,
      level: "basico",
      title: "Tarea 3: Importe del pedido",
      description:
        "En la columna <strong>Importe</strong>: Cantidad × precio. Calcula el precio <strong>con BUSCARV dentro de la fórmula</strong>, sin usar la columna Precio.",
      solution: [
        `Selecciona ${F("P5")}.`,
        "BUSCARV devuelve un número, así que puedes multiplicarlo directamente.",
        `Escribe ${F("=[@Cantidad]*BUSCARV([@Código],ListaPrecios,4,FALSO)")}.`,
      ],
      expectedResult: "Cantidad por precio",
      finalFormula: "=[@Cantidad]*BUSCARV([@Código],ListaPrecios,4,FALSO)",
    },
    {
      id: 4,
      level: "intermedio",
      title: "Tarea 4: Costo de envío por peso",
      description: conditions(
        "En la columna <strong>Costo envío</strong>: busca el <strong>Peso kg</strong> de cada pedido en <strong>TarifasEnvio</strong> (G4). Cada tarifa aplica <strong>desde</strong> su peso:",
        [
          "0 a menos de 1 kg → $99",
          "1 a menos de 5 kg → $149",
          "… y así hasta 30 kg o más → $899",
        ],
        "Los pesos no coinciden exactamente con la tabla: necesitas <strong>coincidencia aproximada</strong>."
      ),
      solution: [
        `Selecciona ${F("Q5")}.`,
        `Con ${F("VERDADERO")}, BUSCARV busca el valor más grande que sea menor o igual al buscado. Por eso la tabla de tarifas debe estar ordenada de menor a mayor.`,
        `Escribe ${F("=BUSCARV([@[Peso kg]],TarifasEnvio,2,VERDADERO)")}.`,
      ],
      expectedResult: "Tarifa del tramo de cada peso",
      finalFormula: "=BUSCARV([@[Peso kg]],TarifasEnvio,2,VERDADERO)",
    },
    {
      id: 5,
      level: "intermedio",
      title: "Tarea 5: Código que no existe",
      description:
        "En <strong>U8</strong>: busca el producto del código de <strong>U4</strong>. Ese código no existe y BUSCARV daría <strong>#N/D</strong>: muestra <strong>No existe</strong> usando <strong>SI.ND</strong>.",
      solution: [
        `Selecciona ${F("U8")}.`,
        `Escribe ${F('=SI.ND(BUSCARV(U4,ListaPrecios,2,FALSO),"No existe")')}.`,
      ],
      expectedResult: "No existe",
      finalFormula: '=SI.ND(BUSCARV(U4,ListaPrecios,2,FALSO),"No existe")',
    },
    {
      id: 6,
      level: "intermedio",
      title: "Tarea 6: Columna calculada con COINCIDIR",
      description: conditions(
        "En la columna <strong>Categoría</strong>: trae la categoría de cada producto, pero <strong>sin escribir el número de columna</strong>:",
        [
          "<strong>COINCIDIR</strong> busca el encabezado \"Categoría\" entre los encabezados de ListaPrecios y devuelve su posición",
          "Esa posición es el número de columna de BUSCARV",
        ],
        "Así, si alguien inserta una columna en la lista de precios, la fórmula sigue funcionando."
      ),
      solution: [
        `Selecciona ${F("R5")}.`,
        `${F("ListaPrecios[#Encabezados]")} es la fila de encabezados de la tabla. ${F('COINCIDIR("Categoría",ListaPrecios[#Encabezados],0)')} devuelve 3.`,
        `Escribe ${F('=BUSCARV([@Código],ListaPrecios,COINCIDIR("Categoría",ListaPrecios[#Encabezados],0),FALSO)')}.`,
      ],
      expectedResult: "Categoría de cada producto",
      finalFormula: '=BUSCARV([@Código],ListaPrecios,COINCIDIR("Categoría",ListaPrecios[#Encabezados],0),FALSO)',
    },
    {
      id: 7,
      level: "reto",
      title: "Tarea 7: Buscar por parte del nombre",
      description:
        "En <strong>U9</strong>: devuelve el <strong>precio</strong> del primer producto cuyo nombre <strong>contenga</strong> el texto de <strong>U5</strong>. BUSCARV acepta comodines en el valor buscado.",
      solution: [
        `Selecciona ${F("U9")}.`,
        "Pero BUSCARV busca en la primera columna (Código), no en Producto. Usa un rango que empiece en Producto: de la columna B a la D, donde Precio es la 3.ª columna.",
        `Escribe ${F('=BUSCARV("*"&U5&"*",ListaPrecios[[Producto]:[Precio]],3,FALSO)')}.`,
      ],
      expectedResult: "Precio del Café molido 1 kg",
      finalFormula: '=BUSCARV("*"&U5&"*",ListaPrecios[[Producto]:[Precio]],3,FALSO)',
    },
  ],
};
